import { NetworkStatus } from "@apollo/client";

import { ExclamationMarkTriangleFilled } from "components/icons/generated";
import IconWithTooltip from "components/IconWithTooltip";
import MetricCard from "components/MetricCard";
import MissingDataBanner from "components/MissingDataBanner";
import Timestamp from "components/Timestamp";
import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import Typography from "ds/components/Typography";
import usePolledQuery from "apollo/usePolledQuery";
import { PollingIntervalGroups } from "apollo/constants";
import { AnalyticsPageDashboard } from "hooks/useAnalytics/pages/dashboard";

import DashboardWidgetsEmptyStateRuns from "../EmptyStateRuns";
import DashboardWidgetsRunTile from "../RunTile";
import DashboardWidgetsRunsSkeleton from "../RunsSkeleton";
import { RUN_TERMINATION_WARNING_WINDOW_DAYS } from "../../constants";
import { Widget } from "../../types";
import { GET_RUNS_TO_BE_TERMINATED } from "./gql";
import { getRunsToBeTerminatedLink } from "./helpers";
import styles from "./styles.module.css";

const ANALYTICS_TITLE = "Runs To Be Terminated Widget";

const DashboardWidgetsRunsToBeTerminated = () => {
  const { loading, data, error, refetch, networkStatus } = usePolledQuery(
    GET_RUNS_TO_BE_TERMINATED,
    {
      pollingGroup: PollingIntervalGroups.Dashboard,
    }
  );

  const isLoading =
    (loading && !data?.metrics?.runsScheduledForTermination) ||
    networkStatus === NetworkStatus.refetch;

  if (error) {
    return (
      <MissingDataBanner
        text="Couldn’t load runs data. Please try to refresh or come back later."
        refreshHandler={refetch}
        fullWidth
      />
    );
  }

  if (isLoading) {
    return <DashboardWidgetsRunsSkeleton />;
  }

  const hasRuns = !!data?.metrics?.hasRuns;
  const runs = data?.metrics?.runsScheduledForTermination?.runs ?? [];
  const totalCount = data?.metrics?.runsScheduledForTermination?.totalCount ?? 0;

  if (!hasRuns) {
    return <DashboardWidgetsEmptyStateRuns showDocs widget={Widget.RunsToBeTerminated} />;
  }

  if (!runs.length) {
    return (
      <DashboardWidgetsEmptyStateRuns
        caption={`No runs are due for termination in the next ${RUN_TERMINATION_WARNING_WINDOW_DAYS} days.`}
        widget={Widget.RunsToBeTerminated}
        onlyCaption
      />
    );
  }

  return (
    <Box direction="column" gap="large" fullWidth>
      <MetricCard fullWidth>
        <Box justify="between" align="start" gap="medium" fullWidth>
          <Box direction="column" gap="medium">
            <Box gap="medium" align="center">
              <Typography variant="p-t7" color="primary" tag="span">
                Total count
              </Typography>
              <IconWithTooltip
                icon={ExclamationMarkTriangleFilled}
                color="warning"
                tooltip="These runs have been waiting long enough to be terminated soon. It is worth investigating what is blocking them."
              />
            </Box>
            <Typography variant="p-t2" tag="span">
              {totalCount}
            </Typography>
            <Typography variant="p-body3" color="secondary" tag="span">
              Due to be terminated within {RUN_TERMINATION_WARNING_WINDOW_DAYS} days
            </Typography>
          </Box>

          <LinkButton
            variant="secondary"
            size="small"
            to={getRunsToBeTerminatedLink()}
            analyticsPage={AnalyticsPageDashboard.Dashboard}
            analyticsTitle={`${ANALYTICS_TITLE} - Metric - Go to Runs Clicked`}
          >
            Go to runs
          </LinkButton>
        </Box>
      </MetricCard>

      <Typography variant="p-t6" tag="span">
        Closest to termination
      </Typography>

      <Box className={styles.runsList}>
        <Box direction="column" gap="medium" fullWidth>
          {runs.map(
            ({
              scheduledTerminationAt,
              runTile: { id, title, state, stackTile, needsApproval },
            }) => (
              <DashboardWidgetsRunTile
                key={id}
                id={id}
                title={title}
                state={state}
                stackTile={stackTile}
                caption={
                  <Typography color="secondary" tag="span" variant="p-body3">
                    Terminates <Timestamp timestamp={scheduledTerminationAt} format="relative" />
                  </Typography>
                }
                needsApproval={needsApproval}
                analyticsTitle={ANALYTICS_TITLE}
              />
            )
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardWidgetsRunsToBeTerminated;
